import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  useDisclosure,
  VisuallyHidden,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { FiEye, FiRotateCcw } from "react-icons/fi";

import { axiosClient } from "../client/http-client";
import { getAuthToken } from "../client/token-utils";
import { DefinitionList, StatusBadge } from "./ui";

interface TicketModalProps {
  ticketId: number;
  /** Row data the list already holds — shown in the header/body while the ticket detail loads. */
  caseNumber?: string;
  subject?: string | null;
  createdDate?: string | null;
  closedDate?: string | null;
  status?: string;
}

/** Label/value bars in the DefinitionList's rhythm, shown while the ticket detail is fetched. */
const DetailsSkeleton = () => (
  <Box aria-busy="true">
    <VisuallyHidden>Loading</VisuallyHidden>
    <Stack spacing={6}>
      {[2, 4].map((count, group) => (
        <Box key={group}>
          <Skeleton speed={1.2} h="0.75rem" w="6rem" mb={3} />
          <SimpleGrid columns={{ base: 1, md: 2 }} columnGap={6}>
            {Array.from({ length: count }).map((_, i) => (
              <Box key={i} py={3} borderBottom="1px solid" borderColor="border.default">
                <Skeleton speed={1.2} h="0.75rem" w="30%" mb={2} />
                <Skeleton speed={1.2} h="1rem" w={i % 2 === 0 ? "70%" : "50%"} />
              </Box>
            ))}
          </SimpleGrid>
        </Box>
      ))}
    </Stack>
  </Box>
);

const TicketModal: React.FC<TicketModalProps> = ({
  ticketId,
  caseNumber,
  subject,
  createdDate,
  closedDate,
  status,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const token = getAuthToken();

  const getUserTicket = async (id: number) => {
    const { data } = await axiosClient.post(
      "/complaints/getTicket",
      {
        ticketId: id,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data.data;
  };

  const { data, isPending, isError, mutate } = useMutation({
    mutationKey: ["ticket", ticketId],
    mutationFn: getUserTicket,
  });

  const caseNo = data?.caseNumber?.value ?? caseNumber;
  const statusValue = data?.status?.text ?? status;
  const email: string | undefined = data?.email?.value || undefined;

  return (
    <>
      <Button
        variant="secondary"
        size="xs"
        leftIcon={<FiEye />}
        aria-label={caseNumber ? `View case ${caseNumber}` : undefined}
        onClick={() => {
          onOpen();
          mutate(ticketId);
        }}
      >
        View
      </Button>

      <Modal onClose={onClose} isOpen={isOpen} isCentered size={"2xl"} motionPreset="slideInBottom" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text textStyle="label" color="text.muted" aria-hidden>
              CASE
            </Text>
            <Flex align="center" gap={3} wrap="wrap" mt={1}>
              <Box as="h2">{caseNo ? `Case #${caseNo}` : "Case"}</Box>
              {statusValue && <StatusBadge kind="ticket" value={statusValue} />}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isPending ? (
              <DetailsSkeleton />
            ) : data ? (
              <Stack spacing={6}>
                <DefinitionList
                  eyebrow="TICKET"
                  items={[
                    { label: "Subject", value: subject },
                    // Placeholder rows: "—" until the ticket endpoint returns these fields.
                    { label: "Description", value: data.message?.value ?? null },
                  ]}
                />
                <DefinitionList
                  columns={2}
                  items={[
                    { label: "Date created", value: createdDate },
                    { label: "Date closed", value: closedDate },
                    { label: "Case category", value: data.caseCategory?.text ?? null },
                    { label: "Case type", value: data.caseType?.text ?? null },
                  ]}
                />
                <DefinitionList
                  eyebrow="CONTACT"
                  columns={2}
                  items={[
                    { label: "Company", value: data.company?.value },
                    { label: "Email", value: email, href: email ? `mailto:${email}` : undefined },
                    { label: "First name", value: data.firstName?.value },
                    { label: "Last name", value: data.lastName?.value },
                    { label: "Phone number", value: data.phoneNumber?.value },
                  ]}
                />
              </Stack>
            ) : isError ? (
              <Alert status="error" alignItems="flex-start">
                <AlertIcon />
                <Box flex="1">
                  <AlertTitle>We couldn't load this ticket</AlertTitle>
                  <AlertDescription>Check your connection and try again.</AlertDescription>
                </Box>
                <Button variant="ghost" size="sm" leftIcon={<FiRotateCcw />} onClick={() => mutate(ticketId)}>
                  Retry
                </Button>
              </Alert>
            ) : null}
          </ModalBody>
          <ModalFooter justifyContent="flex-end">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default TicketModal;
